import React, { useState, useCallback, useImperativeHandle, useEffect } from 'react';
import ConfirmDialog from '../common/ConfirmDialog';
import styled from 'styled-components';
import { auth, functions, httpsCallable, getGroupGoogleDocFile } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { FaUser, FaFileAlt, FaSpinner, FaFolderOpen } from "react-icons/fa";
import { MdDelete, MdWarning, MdKeyboardArrowDown } from "react-icons/md";
import { TbReload } from "react-icons/tb";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useGooglePicker } from '../../utils/useGooglePicker';

async function reconnectGoogleDrive(user, ensureGoogleDriveAccess, runGoogleOAuth) {
    if (!user) return null;
    console.log("Access token invalid — reconnecting Google Drive…");

    const newToken = await ensureGoogleDriveAccess(user, runGoogleOAuth);
    return newToken;
}


function GoogleDocPreview({ previewUrl }) {
    const [previewFailed, setPreviewFailed] = useState(false);

    return (
        <Section>
            <Label>Document Preview</Label>
            {previewFailed ? (
                <FallbackBox>
                    ⚠️ Failed to load document preview.
                    <br />
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                        Open document in new tab
                    </a>
                </FallbackBox>
            ) : (
                <>
                    <StyledIframe
                        src={previewUrl}
                        title="Google Doc Preview"
                        width="100%"
                        height="600"
                        allow="autoplay"
                        onError={() => setPreviewFailed(true)}
                    />
                    <small>
                        Can’t see it?{" "}
                        <a href={previewUrl} target="_blank" rel="noopener noreferrer">
                            Open in new tab
                        </a>
                    </small>
                </>
            )}
        </Section>
    );
}

const GoogleDocSection = (props, ref) => {
    const { docId, googleDocLink, initialMeta, onChange, onMetaFetched, onRemove, isDuplicate, canRemove, isEmpty, onFetchStart } = props;
    const [loading, setLoading] = useState(false);
    //const [error, setError] = useState(null);
    const [meta, setMeta] = useState(null);
    const [formatError, setFormatError] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [expanded, setExpanded] = useState(false);
    const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
    const { user, getGoogleAccessToken, groupInfo, ensureGoogleDriveAccess, saveGoogleAccessToken } = useAuth();
    const toggleExpanded = () => setExpanded((e) => !e);
    const [pickedFileId, setPickedFileId] = useState(null);

    const link = (googleDocLink || "").trim();
    const match = link.match(/docs\.google\.com\/document\/d\/([a-zA-Z0-9-_]+)/);

    // Note: useGooglePicker hook will be defined after handleFetchWithFileId

    const isGoogleUser = user?.providerData?.some(
        (p) => p.providerId === "google.com"
    )

    // If the parent updates meta (like switching sessions), sync it:
    useEffect(() => {
        if (initialMeta && Object.keys(initialMeta).length > 0) {
            setMeta(initialMeta);
        }
    }, [initialMeta]);

    useEffect(() => {
        if (!link) {
            setFormatError(null);
            return; // allow empty
        } else if (!match) {
            setFormatError("❗️Invalid Google Doc link. Format: https://docs.google.com/document/d/<ID>");
        } else if (isDuplicate) {
            // use a stable string instead of JSX to avoid re-renders
            setFormatError("DUPLICATE");
        } else {
            setFormatError(null);
        }
    }, [link, match, isDuplicate]);

    const error = formatError || fetchError;

    const runGoogleOAuth = useCallback(async () => {
        const provider = new GoogleAuthProvider();
        provider.addScope('https://www.googleapis.com/auth/drive.file');

        const hasGoogleProvider = user.providerData.some(
            p => p.providerId === "google.com"
        );

        if (hasGoogleProvider) {
            console.log("🔄 Reauth Google popup (safe, no re-render)");
            const result = await signInWithPopup(auth, provider);
            const token = GoogleAuthProvider.credentialFromResult(result)?.accessToken;

            if (token) {
                saveGoogleAccessToken(token);
            }

            return token;
        }

        throw new Error("NO_GOOGLE_LINKED");
    }, [saveGoogleAccessToken, user]);

    // New function to handle fetch with fileId directly
    const handleFetchWithFileId = useCallback(async (fileId, providedAccessToken = null) => {
        if (!fileId) {
            setFetchError("❗️Please pick a Google Doc from Drive.");
            setLoading(false);
            return { ok: false };
        }

        setLoading(true);
        setFetchError(null);

        // Use provided token (from picker) if available, otherwise get from user
        let accessToken = providedAccessToken || await getGoogleAccessToken(user);

        // If no token but user has Google linked, try to get a fresh token
        if (!accessToken && isGoogleUser) {
            console.log("No access token found but Google is linked. Attempting to get fresh token...");
            try {
                accessToken = await ensureGoogleDriveAccess(user, runGoogleOAuth);
            } catch (err) {
                console.error("Failed to get Google access token:", err);
                if (err.message === "NO_GOOGLE_LINKED") {
                    setFetchError("❗Please connect your Google account first.");
                } else {
                    setFetchError("❗Failed to authenticate with Google. Please try again.");
                }
                setLoading(false);
                return { ok: false };
            }
        }

        if (!accessToken) {
            setFetchError("❗Please connect your Google account first.");
            setLoading(false);
            return { ok: false };
        }

        // Try up to 2 attempts: initial + retry after reconnect
        let attempt = 0;
        while (attempt < 2) {
            try {
                //1. Fetch metadata
                const fetchGoogleDocMeta = httpsCallable(functions, "getGoogleDocMeta");
                const metaRes = await fetchGoogleDocMeta({ fileId: fileId, accessToken: accessToken, });

                // Case: Backend explicitly says "auth invalid"
                if (metaRes?.data?.error?.status === 401 || metaRes?.data?.error?.code === "unauthenticated") {
                    console.warn("Token expired or missing — reconnecting Google Drive…");

                    // Only retry once
                    if (attempt === 0) {
                        accessToken = await reconnectGoogleDrive(user, ensureGoogleDriveAccess, runGoogleOAuth);
                        attempt++;
                        continue; // retry fetch
                    }

                    setFetchError("❗Google re-authentication failed.");
                    return { ok: false };
                }

                if (metaRes.data.success === false) {
                    const code = metaRes.data.error?.code;
                    switch (code) {
                        case "access-denied":
                            setFetchError("❗️Access denied. Please at least have Editor access or make it public.");
                            break;
                        case "unsupported-type":
                            setFetchError("❗️Only Google Docs are supported (not PDFs or Sheets).");
                            break;
                        case "not-found":
                            setFetchError("❗️File not found. Please verify your document link. Please be one of the doc contributor");
                            break;
                        default:
                            setFetchError("❗️Failed to fetch Google Doc metadata. Please try again.");
                    }
                    setLoading(false);
                    setMeta(null);
                    return { ok: false };
                }
                const data = metaRes.data.data;
                const currentWordCount = data.wordCount || 0;

                //2. Check if file ID exists in group files to get last stored word count
                const fileCheckRes = await getGroupGoogleDocFile(groupInfo.groupId, fileId);
                let lastStoredWordCount = null;
                if (!fileCheckRes.success) {
                    console.error("Failed to fetch group Google Doc info:", fileCheckRes.message);
                    return { ok: false };
                } else if (fileCheckRes.found) {
                    lastStoredWordCount = fileCheckRes.lastWordCount;
                }

                console.log("currentWordCount:", currentWordCount);
                console.log("lastStoredWordCount:", lastStoredWordCount);

                //3. Calculate word count changes based on stored word count
                let wordChange = null;

                // Case 1: No stored word count - this is the first time (show null)
                if (lastStoredWordCount === null || lastStoredWordCount === undefined) {
                    wordChange = null;
                }
                // Case 2: We have stored word count - calculate the difference
                else {
                    const wordDiff = currentWordCount - lastStoredWordCount;
                    if (wordDiff > 0) {
                        // Words were added
                        wordChange = { added: wordDiff, removed: 0 };
                    } else if (wordDiff < 0) {
                        // Words were removed
                        wordChange = { added: 0, removed: Math.abs(wordDiff) };
                    } else {
                        // No change in word count (same doc - show 0)
                        wordChange = { added: 0, removed: 0 };
                    }
                    console.log("Word count comparison:", wordChange, `(from ${lastStoredWordCount} to ${currentWordCount})`);
                }


                //4. Save metadata to state (always include wordChange field)
                const dataWithChanges = {
                    ...data,
                    wordChange: wordChange, // Always include wordChange field (null for initial, {added:0,removed:0} for same, or actual changes)
                };
                setMeta(dataWithChanges);
                setExpanded(false);
                onMetaFetched(docId, dataWithChanges);
                return { ok: true, meta: dataWithChanges };
            } catch (err) {
                console.error("Error fetching Google Doc meta:", err);
                if (err.message === "NO_GOOGLE_LINKED") {
                    setFetchError("❗Please connect your Google account first.");
                    return;
                }
                if (err?.code === 401 || err?.status === 401) {
                    if (attempt === 0) {
                        accessToken = await reconnectGoogleDrive(user, ensureGoogleDriveAccess, runGoogleOAuth);
                        attempt++;
                        continue;
                    }
                }

                setFetchError("❗️Failed to fetch Google Doc metadata. Please check the link.");
                return { ok: false, error: err };
            } finally {
                setLoading(false); // Always called, no matter what
            }
        }
    }, [
        docId,
        isGoogleUser,
        onMetaFetched,
        user,
        getGoogleAccessToken,
        ensureGoogleDriveAccess,
        runGoogleOAuth,
        groupInfo?.groupId,
    ]);

    // Add Google Picker hook - defined after handleFetchWithFileId
    const { showPicker, pickerLoaded, loadError } =
        useGooglePicker(
            useCallback((fileUrl, fileId, accessToken) => {
                onChange(docId, fileUrl);
                setPickedFileId(fileId);
                if (accessToken) {
                    saveGoogleAccessToken(accessToken);
                }

                setTimeout(() => {
                    if (onFetchStart) onFetchStart();
                    handleFetchWithFileId(fileId, accessToken);
                }, 300);
            }, [docId, onChange, onFetchStart, handleFetchWithFileId, saveGoogleAccessToken]),
            {
                // Must be synchronous — async breaks the user-gesture context
                // and causes the GIS OAuth popup to open behind the page.
                getAccessToken: () => {
                    const stored = localStorage.getItem("g_access_token");
                    const expiry = parseInt(localStorage.getItem("g_access_token_expiry") || "0", 10);
                    return stored && Date.now() < expiry ? stored : null;
                },
                onAccessToken: saveGoogleAccessToken,
            }
        );


    // Update handleFetch to use pickedFileId or extract from link
    const handleFetch = useCallback(async () => {
        const fileId = pickedFileId || (() => {
            const fileIdMatch = googleDocLink.match(/\/document\/d\/([a-zA-Z0-9-_]+)/);
            return fileIdMatch?.[1];
        })();

        if (!fileId) {
            setFetchError("❗️Please pick a Google Doc from Drive.");
            setLoading(false);
            return { ok: false };
        }

        return handleFetchWithFileId(fileId);
    }, [pickedFileId, googleDocLink, handleFetchWithFileId]);


    const resetGoogleDocField = useCallback(() => {
        onMetaFetched(null);
        setMeta(null);
    }, [onChange, onMetaFetched]);

    // Expose the method to parent
    useImperativeHandle(ref, () => ({
        fetchNow: handleFetch,     // parent can call ref.current.fetchNow()
        reset: () => {             // (optional) expose other actions
            setMeta(null);
            setFetchError(null);
        },
    }), [handleFetch]);



    return (
        <DocSection>
            <PickerSection>
                {!meta && (
                    <PickerButtonWrapper>
                        <PickerButton
                            type="button"
                            onClick={showPicker}
                            disabled={!pickerLoaded || loading || !!loadError}
                            title={
                                loadError
                                    ? `Error: ${loadError}`
                                    : "Pick Google Doc from Drive"
                            }
                        >
                            <FaFolderOpen />
                            <span>Pick from Google Drive</span>
                        </PickerButton>
                        {loading && <LoadingBar />}
                        {canRemove && isEmpty && (
                            <RemoveInlineButton disabled={loading} onClick={() => onRemove?.(docId)}>
                                <MdDelete />
                            </RemoveInlineButton>
                        )}
                    </PickerButtonWrapper>
                )}

                    {googleDocLink && (
                        <SelectedDocInfo>
                            <span>Selected: </span>
                            <DocLink href={googleDocLink} target="_blank" rel="noopener noreferrer">
                                {googleDocLink}
                            </DocLink>
                            <IconButton
                                type="button"
                                onClick={() => {
                                    if (onFetchStart) onFetchStart();
                                    handleFetch();
                                }}
                                disabled={loading}
                                title="Fetch Doc Info"
                            >
                                {loading ? (
                                    <FaSpinner className="spin" />
                                ) : (
                                    <TbReload />
                                )}
                            </IconButton>
                            {canRemove && isEmpty && (
                                <RemoveInlineButton
                                    type="button"
                                    disabled={loading}
                                    onClick={() => onRemove?.(docId)}
                                >
                                    <MdDelete />
                                </RemoveInlineButton>
                            )}
                        </SelectedDocInfo>
                    )}
            </PickerSection>
            {error === "DUPLICATE" ? (
                <ErrorMessage>
                    ❗️This Google Doc is already added:{" "}
                    <a
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "underline" }}
                    >
                        View Document
                    </a>
                </ErrorMessage>
            ) : error ? (
                <ErrorMessage>{error}</ErrorMessage>
            ) : null}

            {meta && !fetchError && (
                <ToggleButton
                    type="button"
                    onClick={toggleExpanded}
                    aria-expanded={expanded}
                    aria-controls={`gdoc-card-${docId}`}
                    title={expanded ? "Collapse details" : "Expand details"}
                >
                    <ChevronIcon $open={expanded} />
                    <span>{expanded ? "Hide" : "Show"} fetched details</span>
                </ToggleButton>
            )}

            {meta && Object.keys(meta).length > 0 && (
                <Collapsible
                    id={`gdoc-card-${docId}`}
                    aria-hidden={!expanded}
                    $open={expanded}
                >
                    <Card>
                        <TitleRow>
                            <Label>Google Doc Title</Label>
                            <GoogleDocLinkAnchor href={meta.link} target="_blank" rel="noopener noreferrer">
                                View Google Docs
                            </GoogleDocLinkAnchor>
                        </TitleRow>
                        <Header>{meta.title}</Header>
                        <Section>
                            <Label>Google Doc Metadata</Label>
                            <MetaGrid>
                                <MetaBox color={{ bg: "#eff6ff", border: "#bfdbfe", text: "#3b82f6", value: "#1d4ed8" }}>
                                    <IconWrapper><FaFileAlt size={24} /></IconWrapper>
                                    <MetaText color={{ text: "#3b82f6", value: "#1d4ed8" }}>
                                        <p>Total Word Count</p>
                                        <p>{meta.wordCount}</p>
                                    </MetaText>
                                </MetaBox>
                                <MetaBox color={{ bg: "#fef2f2", border: "#fecaca", text: "#dc2626", value: "#b91c1c" }}>
                                    <IconWrapper><FaUser className="text-blue-600 mr-3" size={24} /></IconWrapper>
                                    <MetaText color={{ text: "#dc2626", value: "#b91c1c" }} small={!meta.wordChange || (meta.wordChange.added === 0 && meta.wordChange.removed === 0)} >
                                        {meta.wordChange ? (
                                            meta.wordChange.added === 0 && meta.wordChange.removed === 0 ? (
                                                // If wordChange exists and both added/removed are 0 → No change
                                                <>
                                                    <p>Word Changes</p>
                                                    <p>Same Document</p>
                                                </>
                                            ) : (
                                                // If wordChange exists and added/removed > 0 → Show word diffs
                                                <>
                                                    <p>Word Changes</p>
                                                    <CodeChangeLine>
                                                        <Green>+{meta.wordChange.added}</Green>
                                                        <Divider>/</Divider>
                                                        <Red>-{meta.wordChange.removed}</Red>
                                                    </CodeChangeLine>
                                                </>
                                            )
                                        ) : (
                                            // If no wordChange exists → Initial Version
                                            <>
                                                <p>Initial Version</p>
                                                <p>First submission</p>
                                            </>
                                        )}
                                    </MetaText>
                                </MetaBox>
                            </MetaGrid>
                        </Section>
                        <WarningNote>
                            <MdWarning size={18} />
                            Google Doc collaborators are no longer fetched automatically.
                            Please manually select contributors in the Activity Card.
                        </WarningNote>
                        {(meta?.pdfStorage?.url || meta?.previewUrl) && (
                            <GoogleDocPreview previewUrl={meta.pdfStorage?.url || meta.previewUrl} />
                        )}

                        <Section>
                            <RemoveButtonWrapper>
                                <RemoveButton
                                    type="button"
                                    onClick={() => setShowRemoveConfirm(true)}
                                >
                                    Remove Google Doc
                                </RemoveButton>
                            </RemoveButtonWrapper>
                        </Section>
                    </Card>
                </Collapsible>
            )}
            {showRemoveConfirm && (
                <ConfirmDialog
                    title="Remove Google Doc?"
                    message="Are you sure you want to remove this Google Doc info? This action cannot be undone."
                    confirmText="Yes, Remove"
                    cancelText="No, Go Back"
                    variant="danger"
                    onConfirm={() => { setShowRemoveConfirm(false); resetGoogleDocField(); onRemove(docId); }}
                    onCancel={() => setShowRemoveConfirm(false)}
                />
            )}
        </DocSection>
    );
};

export default React.memo(React.forwardRef(GoogleDocSection));

const DocSection = styled.div`
   margin-top: 8px;
   margin-bottom: 8px;
`;

const ErrorMessage = styled.div`
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 0.25rem;
  margin-bottom: 0.5rem;
`;

// Doc Meta Card
const Card = styled.div`
  background: white;
  padding: 2rem 2rem 1rem 2rem;
  border-radius: 0.75rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.08);
  margin-top: 1rem;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  background-color: #ffffff; 
  border: 1px solid #e5e7eb;
`;

const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const GoogleDocLinkAnchor = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  color: #0b99ff;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const Header = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const Label = styled.small`
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6b7280; /* Tailwind gray-500 */
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;


//Meta data
const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const MetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const MetaBox = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'full',
})`
  display: flex;
  align-items: center;
  background: ${({ color }) => color.bg};
  border: 1px solid ${({ color }) => color.border};
  padding: 1rem;
  border-radius: 0.5rem;
  grid-column: ${({ full }) => (full ? "span 2" : "auto")};
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const MetaText = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'color' && prop !== 'small',
})`
  p:first-child {
    font-size: ${({ small }) => (small ? '0.875rem' : '0.875rem')};
    color: ${({ color }) => color.text};
    font-weight: 500;
  }

  p:last-child {
    font-size: ${({ small }) => (small ? '0.95rem' : '1.5rem')};
    font-weight: 700;
    color: ${({ color }) => color.value};
  }
`;

const FallbackBox = styled.div`
  padding: 1rem;
  background-color: #fef2f2;
  color: #991b1b;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  text-align: center;
  font-size: 0.95rem;

  a {
    color: #2563eb;
    text-decoration: underline;
  }
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 600px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
`;


const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

const RemoveButton = styled.button`
  background-color: #fee2e2;
  color: #b91c1c;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 600;
  border: 1px solid #fca5a5;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #fecaca;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.5);
  }
`;

const LoadingBar = styled.div`
  height: 4px;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  background-color: #3b82f6;
  animation: growProgress 1.5s ease-out infinite;
  transform-origin: center;


  @keyframes growProgress {
    0% { width: 0%; opacity: 0.4; }
    50% { width: 60%; opacity: 1; }
    100% { width: 95%; opacity: 0.4; }
  }
`;


const CodeChangeLine = styled.p`
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
`;

const Green = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #16a34a;
`;

const Red = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: #dc2626;
`;

const Divider = styled.span`
  color: #6d28d9; /* match purple if needed */
  padding: 0 0.2rem; /* adjust spacing */
  font-weight: 700;
`;

const RemoveInlineButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid transparent;
  background: transparent;
  color: #ff4c4c;
  height: 100%;
  cursor: pointer;
  transition: color 0.2s ease;

  /* Keep the SVG at an exact pixel size */
  svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
  }

  &:hover {
    color:rgb(195, 3, 3);
  }

  &:disabled {
    color: #ccc;
    cursor: not-allowed;
  }
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 44px;
  width: 44px;
  border-radius: 10px;
  border: 1px solid #e4e4e4;
  background: #534af5;
  cursor: pointer;
  color: white;
  padding: 12px;
  transition: background 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;

  &:hover { 
    background: #3f35f6ff;
 }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  svg { width: 22px; height: 22px; transition: transform 0.2s ease;}

  &:hover svg {
    transform: scale(1.2); /* zoom only the icon */
  }

  .spin {
    animation: spin 0.9s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); } to { transform: rotate(360deg); }
  }
`;

const Collapsible = styled.div.withConfig({
    shouldForwardProp: (p) => p !== '$open',
})`
  overflow: hidden;
  transition: max-height 280ms ease, opacity 200ms ease;
  max-height: ${({ $open }) => ($open ? '2400px' : '0px')};
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  will-change: max-height, opacity;
`;

// Toggle button next to reload
const ToggleButton = styled.button`
  display: flex;
  margin-top: 8px;
  border: none;
  align-items: center;
  color: #534af5; /* text-gray-600 */
  background-color: #f3f4f6; /* bg-gray-100 */
  padding: 0.5rem 1rem; /* py-2 px-4 */
  border-radius: 0.5rem; /* rounded-lg */
  transition: color 0.2s ease;

  &:hover {
    color: #352cedff; /* hover:text-gray-800 */
  }

  span { 
    margin-left: 0.25rem; /* ml-2 */
    font-size: 0.875rem; /* text-sm */
    font-weight: 500; /* font-medium */ }
`;

const ChevronIcon = styled(MdKeyboardArrowDown).withConfig({
    shouldForwardProp: (p) => p !== '$open',
})`
  margin-left: -4px;
  font-size: 24px;
  transition: transform 0.2s ease;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const WarningNote = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  background: #fefce8; /* warm amber background */
  border: 1px solid #fef08a; /* amber-300 */
  color: #a16207; /* amber-700 */
  padding: 12px 14px;
  border-radius: 8px;
  font-size: 0.875rem;
  line-height: 1.3;
  margin-bottom: 1rem;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const PickerSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 8px;
`;

const PickerButtonWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const PickerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 11px 28px;
  background: #534af5;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  width: 100%;
  
  &:hover:not(:disabled) {
    background: #3f35f6;
  }
  
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const SelectedDocInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 12px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 14px;
  
  span {
    color: #6b7280;
    font-weight: 500;
  }
`;

const DocLink = styled.a`
  color: #534af5;
  text-decoration: underline;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  &:hover {
    text-decoration: none;
  }
`;
